import type { ReactNode } from "react";
import { clamp } from "@common/helpers/collections";
import { addCalendarDays } from "@common/helpers/date";
import { pluralizeWithCount } from "@common/helpers/string";
import { formatDateOnly } from "@helpers/date";
import { isMonthStart } from "./densityBuckets.lib";

export const PLOT_HEIGHT = 80;

export interface DensityChartSeries {
    id: string;
    label: string;
    /** Any CSS color, including `var(--token)`. */
    color: string;
    /** A stronger shade for small marks naming the series, like the tooltip's dot; defaults to `color`. */
    accentColor?: string;
    /** A highlighted segment's fill, darker than `color`; defaults to `color`. */
    selectedColor?: string;
    /** A hovered segment's fill, between `color` and `selectedColor`; defaults to `color`. */
    hoverColor?: string;
    /** Replaces the tooltip's dot, e.g. a 16px avatar. */
    icon?: ReactNode;
}

/** Values per series for one bar, keyed by its first calendar day (`YYYY-MM-DD`): the day, its week's Monday or its month's 1st. */
export interface DensityChartDay {
    date: string;
    values: Partial<Record<string, number>>;
}

/** A selected run of calendar days, both ends inclusive. */
export interface DensityChartWindow {
    start: string;
    end: string;
}

/** A run of drawn bars, `end` exclusive. Fractional while dragging. */
export interface BarSpan {
    start: number;
    end: number;
}

export interface DensityChartPeriod extends DensityChartWindow {
    /** Days. */
    count: number;
}

export interface DensityChartPeriodLabel {
    range: string;
    duration?: string;
}

export interface DensityChartBar {
    /** The bar's days: one, a week or a month. */
    window: DensityChartWindow;
}

export interface DensityChartSlice extends DensityChartBar {
    seriesId: string;
}

/** The segments drawn in their series' `selectedColor`: those of `seriesIds` in the bars sharing a day with `range`. Either left out means any. */
export interface DensityChartHighlight {
    range?: DensityChartWindow;
    seriesIds?: ReadonlySet<string>;
}

/** Whether the segment of `seriesId` in the bar covering `bar` is highlighted. */
export const isSegmentHighlighted = ({
    highlight,
    seriesId,
    bar,
}: {
    highlight: DensityChartHighlight | undefined;
    seriesId: string;
    bar: DensityChartWindow;
}) =>
    highlight !== undefined &&
    (highlight.range === undefined ||
        (highlight.range.start <= bar.end &&
            bar.start <= highlight.range.end)) &&
    (highlight.seriesIds === undefined || highlight.seriesIds.has(seriesId));

/** The window, starting no earlier than the history start. */
export const clampToHistory = (
    window: DensityChartWindow,
    startDate: string,
): DensityChartWindow =>
    window.start < startDate ? { ...window, start: startDate } : window;

export const formatDay = (date: string) => formatDateOnly(date, "MMM d");

const formatDayWithYear = (date: string) => formatDateOnly(date, "MMM d, yyyy");

const isWholeMonth = ({ start, end }: DensityChartWindow) =>
    isMonthStart(start) &&
    end.slice(0, 7) === start.slice(0, 7) &&
    isMonthStart(addCalendarDays(end, 1));

/** "Oct 8", "Oct 8 – Oct 14", a whole month "Oct 2026", or across years "Dec 29, 2025 – Jan 4, 2026". */
export function formatDensityRange(window: DensityChartWindow): string {
    const { start, end } = window;
    if (start === end) {
        return formatDay(start);
    }
    if (isWholeMonth(window)) {
        return formatDateOnly(start, "MMM yyyy");
    }
    if (start.slice(0, 4) !== end.slice(0, 4)) {
        return `${formatDayWithYear(start)} – ${formatDayWithYear(end)}`;
    }
    return `${formatDay(start)} – ${formatDay(end)}`;
}

/** Periods read like "Oct 8 – Oct 14" and "7 days". */
export function formatDensityPeriod(
    period: DensityChartPeriod,
): DensityChartPeriodLabel {
    return {
        range: formatDensityRange(period),
        duration: pluralizeWithCount("day", period.count),
    };
}

/** The design's 4px gap between 14px bars, as a share of each bar's band. */
export const BAR_GAP_SHARE = 4 / 18;

export interface BarTap {
    barIndex: number;
    /** Px from the top of the track. */
    y: number;
}

interface BarTapAtPointParams {
    rect: Pick<DOMRect, "left" | "top" | "width">;
    point: Pick<MouseEvent, "clientX" | "clientY">;
    barCount: number;
}

export function barTapAtPoint({
    rect,
    point,
    barCount,
}: BarTapAtPointParams): BarTap {
    const y = point.clientY - rect.top;
    if (barCount <= 0 || rect.width <= 0) {
        return { barIndex: 0, y };
    }
    const share = (point.clientX - rect.left) / rect.width;
    return {
        barIndex: clamp(Math.floor(share * barCount), 0, barCount - 1),
        y,
    };
}
